<template>
    <confirm-dialog ref="confirmVerification"></confirm-dialog>
    <div v-if="errorMessage" class="alert alert-danger"> {{ errorMessage }}</div>

    <card label="Companies House">
        <div class="row">
            <div class="col">
                <button v-if="isVerificationEditor" type="button" class="btn-primary btn float-left mb-2 mt-4"
                    @click="saveVerification">
                    Verification
                </button>
            </div>
        </div>
        <div v-if="verificationModel.verificationScheduleDate" class="row">
            <label class="form-label">Next Scheduled Date for Verification</label>
            <div class="col-6">
                <div v-if="verificationModel.scheduleDate">
                    <datepicker id="nextScheduledDate" class="form-control mb-4 w-50" :disabled="true"
                        v-model="verificationModel.scheduleDate" />
                </div>

                <div v-if="!verificationModel.scheduleDate">
                    <input class="form-control mb-4 w-50" id="nextScheduledDate" value="Verification date not scheduled"
                        disabled />
                </div>
            </div>
        </div>
    </card>
    <card v-if="model" label="Cease to Trade">
        <div class="row">
            <div class="col">
                <label class="form-label">Cease to Trade Date</label>
                <datepicker
                    id="ceaseToTradeDate"
                    v-model="model.ceaseToTradeDate"
                    class="form-control"
                    :style="{ background: isCeaseToTradeEditor ? 'white' : 'var(--bs-secondary-bg)' }"
                    :disabled="!isCeaseToTradeEditor"
                    :input-format="inputFormat"
                />
            </div>
            <div class="col">
                <smart-select
                    id="tradingStatus"
                    v-model="model.tradingStatus"
                    label="Trading Status"
                    :options="tradingStatusOptions"
                    :disabled="!isCeaseToTradeEditor"
                    :is-required="false"
                />
            </div>
        </div>

        <div class="row mt-3">
            <div class="col">
                <label class="form-label">Cease to Trade Evidence</label>
                <div v-if="model.ceaseToTradeEvidences && model.ceaseToTradeEvidences.length > 0" class="d-flex flex-column gap-2">
                    <div v-for="(evidence, index) in model.ceaseToTradeEvidences" :key="index" >
                        <scheme-business-file-upload :disabled="!isCeaseToTradeEditor" :model-value="evidence" label="" :show-file-input="false" @update:model-value="(file) => updateExistingFile(index, file)" />
                    </div>
                </div>
                <div v-if="!model.ceaseToTradeEvidences || model.ceaseToTradeEvidences.length === 0" class="alert alert-warning">
                    No Evidence Uploaded
                </div>
            </div>
        </div>
        <scheme-business-file-upload v-if="isCeaseToTradeEditor" @update:model-value="addFileToModel" label="New Evidence" />

        <div class="d-flex items-center gap-2">
            <button v-if="isCeaseToTradeEditor" type="button" class="btn-primary btn float-left mb-2 mt-4"
                @click="saveCeaseToTrade">
                Set Cease to Trade
            </button>
            <button v-if="isCeaseToTradeEditor" type="button" class="btn-secondary btn float-left mb-2 mt-4"
                @click="enableBusiness">
                Enable Business
            </button>
        </div>
    </card>
</template>

<script>
import { confirmType } from '@trustmark-2005-ltd/trustmark-shared-components';
import { ConfirmDialog, Card, SmartSelect } from '@trustmark-2005-ltd/trustmark-shared-components';
import Datepicker from 'vue3-datepicker';
import SchemeBusinessFileUpload from './SchemeBusinessFileUpload.vue';

export default {
    name: 'SchemeBusinessVerification',
    components: {
        ConfirmDialog,
        Card,
        Datepicker,
        SmartSelect,
        SchemeBusinessFileUpload,
    },
    data() {
        return {
            errorMessage: null,
        };
    },
    computed: {
        currentAccount() {
            return this.$store.state.auth.account;
        },
        isVerificationEditor() {
            return this.currentAccount && this.currentAccount.roles
                ? this.currentAccount.roles.includes('VerificationEditor')
                : false;
        },
        isCeaseToTradeEditor() {
            return this.currentAccount && this.currentAccount.roles
                ? this.currentAccount.roles.includes('CeaseToTradeEditor')
                : false;
        },
        model() {
            return this.$store.state.schemeBusiness?.business?.schemeBusiness;
        },
        verificationModel() {
            return this.$store.state.schemeBusiness;
        },
        inputFormat() {
            return 'dd/MM/yyyy';
        },
        tradingStatusOptions() {
            return [
                'Dissolved', 'Liquidation', 'Removed', 'Other',
            ];
        },
    },
    watch: {
        model: {
            handler(newValue) {
                if (newValue && newValue.ceaseToTradeDate) {
                    newValue.ceaseToTradeDate = new Date(newValue.ceaseToTradeDate);
                }
            },
            immediate: true,
        },
    },
    methods: {
        addFileToModel({ fileUrl, fileName }) {
            this.model.ceaseToTradeEvidences = this.model.ceaseToTradeEvidences || [];
            this.model.ceaseToTradeEvidences.push({
                fileUrl,
                fileName,
            });
        },
        updateExistingFile(index, { fileUrl, fileName }) {
            if (!this.model.ceaseToTradeEvidences || !this.model.ceaseToTradeEvidences[index]) {
                return;
            }
            if (fileUrl === '') {
                // File was deleted, remove from array
                this.model.ceaseToTradeEvidences.splice(index, 1);
                return;
            }
            this.model.ceaseToTradeEvidences[index] = {
                fileUrl,
                fileName,
            }
        },
        async saveCeaseToTrade() {
            if (!this.$route.params.schemeBusinessId || !this.$route.params.schemeId) {
                window.scrollTo(0, 0);
                return;
            }
            const payload = {
                schemeBusinessId: this.$route.params.schemeBusinessId,
                schemeId: this.$route.params.schemeId,
                ceaseToTradeDate: this.model.ceaseToTradeDate,
                tradingStatus: this.model.tradingStatus,
                ceaseToTradeEvidences: this.model.ceaseToTradeEvidences,
            }
            this.$refs.confirmVerification
                .show({
                    title: 'Cease to Trade',
                    message: 'Are you sure you sure you want to set this business to cease to trade?',
                    confirmType: confirmType.standard,
                    okButtonText: 'Cease to Trade',
                })
                .then((result) => {
                    if (result === true) {
                        this.$store.dispatch('schemeBusiness/updateCeaseToTrade', payload)
                            .then(() => {
                                window.scrollTo(0, 0);
                                this.$store.dispatch('schemeBusiness/get', {
                                    schemeId: this.$route.params.schemeId,
                                    schemeBusinessId: this.$route.params.schemeBusinessId,
                                });
                            })
                            .catch((err) => {
                                window.scrollTo(0, 0);
                                this.errorMessage =
                                    (err.response && err.response.data && err.response.data.message) ||
                                    err.message ||
                                    err.toString();
                            });
                    }
                });
        },
        async enableBusiness() {
            if (!this.$route.params.schemeBusinessId || !this.$route.params.schemeId) {
                window.scrollTo(0, 0);
                return;
            }
            const payload = {
                schemeBusinessId: this.$route.params.schemeBusinessId,
                schemeId: this.$route.params.schemeId,
                ceaseToTradeDate: null,
                tradingStatus: null,
                ceaseToTradeEvidences: [],
            }
            this.$refs.confirmVerification
                .show({
                    title: 'Enable Business',
                    message: 'Are you sure you sure you want to enable this business - all cease to trade settings will be cleared?',
                    confirmType: confirmType.standard,
                    okButtonText: 'Enable Business',
                })
                .then((result) => {
                    if (result === true) {
                        this.$store.dispatch('schemeBusiness/updateCeaseToTrade', payload)
                            .then(() => {
                                window.scrollTo(0, 0);
                                this.$store.dispatch('schemeBusiness/get', {
                                    schemeId: this.$route.params.schemeId,
                                    schemeBusinessId: this.$route.params.schemeBusinessId,
                                });
                            })
                            .catch((err) => {
                                window.scrollTo(0, 0);
                                this.errorMessage =
                                    (err.response && err.response.data && err.response.data.message) ||
                                    err.message ||
                                    err.toString();
                            });
                    }
                });
        },
        async saveVerification() {
            if (!this.$route.params.schemeBusinessId || !this.$route.params.schemeId) {
                window.scrollTo(0, 0);
                return;
            }
            const payload = {
                schemeBusinessId: this.$route.params.schemeBusinessId,
                schemeId: this.$route.params.schemeId
            }

            this.$refs.confirmVerification
                .show({
                    title: 'Verification',
                    message: 'Are you sure you sure you want to start the verification process?',
                    confirmType: confirmType.standard,
                    okButtonText: 'Verification',
                })
                .then((result) => {
                    if (result === true) {
                        this.$store.dispatch('schemeBusiness/createVerification', payload)
                            .then(() => {
                                window.scrollTo(0, 0);
                            })
                            .catch((err) => {
                                window.scrollTo(0, 0);
                                this.loading = false;
                                this.errorMessage =
                                    (err.response && err.response.data && err.response.data.message) ||
                                    err.message ||
                                    err.toString();
                            });
                    }
                });
        },
    },
    async mounted() {
        if (!this.model) {
            await this.$store.dispatch('schemeBusiness/getVerificationScheduleDate', { schemeBusinessId: this.$route.params.schemeBusinessId, schemeId: this.$route.params.schemeId });
        }
        if (this.model && this.model.scheduleDate) {
            this.model.scheduleDate = new Date(this.model.scheduleDate);
        }
    }
}
</script>
